import { useCallback, useEffect, useRef, useState } from "react";
import { portalApi } from "../api";
import { codeOf } from "../errors";
import { useI18n } from "../i18n";
import { genrePath } from "../routes";
import { Rail, TitleGrid, usePagedTitles } from "../shelf";
import { usePortal } from "../state";
import type { GenreCount, StoreRail } from "../types";
import { EmptyState, Link, Loading, Notice, PageTitle } from "../ui";
import { ChevronLeft, ChevronRight, FilmIcon, SearchIcon } from "../icons";
import { searchable } from "../search";
import { InformationRail } from "../information";

/**
 * Browse: the catalogue page (slice H; the shop retired for the
 * organisation product, docs/design/organisation-plans.md).
 *
 * Top to bottom: the genres as a row of chips (each a genre page,
 * `/genre/:id`), then the carousels from GET /rails - "Alla filmer" by
 * popularity, then one per genre - each fetching its own titles only when it
 * nears the viewport (`shelf.tsx`). The whole catalogue is shown: there is no
 * per-title shop any more, so nothing is narrowed to what an offer sells.
 *
 * Search-as-you-type lives in the HEADER (`chrome.tsx`, signed-in
 * purchasers): 250 ms after the last keystroke, with at least two letters or
 * digits, the query goes into the address bar (`/?q=`, replaced, so back
 * from a title returns to the results and a refresh keeps them) and the
 * results replace the rails in a grid here. A newer query aborts the request
 * still in flight for the older one. Clearing restores the rails.
 *
 * The catalogue routes are public with an OPTIONAL session (see
 * `readTitles`): a member's own titles show as rented.
 */

/** The genres as a swipeable row of chips, with arrows where there is a pointer. */
function GenreChips({ genres }: { genres: GenreCount[] }) {
  const { t } = useI18n();
  const trackRef = useRef<HTMLUListElement>(null);
  const scroll = (dir: 1 | -1) => {
    const track = trackRef.current;
    if (!track || typeof track.scrollBy !== "function") return;
    const reduce =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    track.scrollBy({
      left: dir * Math.max(160, track.clientWidth * 0.7),
      behavior: reduce ? "auto" : "smooth",
    });
  };
  return (
    <nav className="chips" aria-labelledby="genres-h">
      <div className="chips__head">
        <h2 id="genres-h" className="chips__title">
          {t("store.genres")}
        </h2>
        {genres.length > 4 && (
          <div className="rail__nav">
            <button
              type="button"
              className="rail__btn"
              onClick={() => scroll(-1)}
              aria-label={t("store.genres.prev")}
            >
              <ChevronLeft />
            </button>
            <button
              type="button"
              className="rail__btn"
              onClick={() => scroll(1)}
              aria-label={t("store.genres.next")}
            >
              <ChevronRight />
            </button>
          </div>
        )}
      </div>
      <div className="chips__frame">
        <ul className="chips__track" ref={trackRef}>
          {genres.map((g) => (
            <li key={g.id}>
              <Link className="chip" to={genrePath(g.id)}>
                {g.label}
                <span className="chip__count">{g.count}</span>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </nav>
  );
}

export function BrowseScreen({ q: routeQ }: { q: string }) {
  const { t, lang } = useI18n();
  const { replace, context } = usePortal();
  const [rails, setRails] = useState<StoreRail[] | null>(null);
  const [genres, setGenres] = useState<GenreCount[]>([]);
  // The Content API could not list a carried provider: say so, never pass a
  // short (or empty) shelf off as the whole catalogue.
  const [incomplete, setIncomplete] = useState(false);
  const [truncated, setTruncated] = useState(false);
  const [failed, setFailed] = useState<false | "error" | "unavailable">(false);

  // The committed query is the one in the address bar (the header's search
  // box puts it there).
  const query = searchable(routeQ);
  const results = usePagedTitles(query ? { q: query } : null);

  const load = useCallback(async () => {
    setFailed(false);
    try {
      const [r, g] = await Promise.all([
        portalApi.rails({ lang }),
        portalApi.genres({ lang }),
      ]);
      setRails(r.rails);
      setGenres(g.genres);
      setIncomplete(!!r.unavailable);
      setTruncated(!!r.truncated);
    } catch (err) {
      setFailed(
        codeOf(err) === "catalogue_unavailable" ? "unavailable" : "error"
      );
    }
  }, [lang]);

  useEffect(() => {
    void load();
  }, [load]);

  if (failed && !rails) {
    return (
      <section className="panel">
        <Notice kind="error">
          {t(failed === "unavailable" ? "browse.unavailable" : "common.error")}
        </Notice>
        <button type="button" className="btn" onClick={() => void load()}>
          {t("common.retry")}
        </button>
      </section>
    );
  }
  if (!rails) return <Loading />;

  const total = results.page?.pagination.total ?? 0;
  const empty = rails.length === 0 && !incomplete;
  return (
    <>
      {/* The page's h1, for readers and the focus on arrival. */}
      <PageTitle className="sr-only">{context.portal.name}</PageTitle>
      {incomplete && <Notice kind="error">{t("browse.unavailable")}</Notice>}

      {query ? (
        <section aria-labelledby="results-h" className="results">
          <h2 id="results-h" className="section-title" aria-live="polite">
            {results.page
              ? total === 1
                ? t("store.search.hit")
                : t("store.search.hits", { count: total })
              : t("common.loading")}
          </h2>
          {results.error && (
            <Notice kind="error">
              {t(
                results.error === "catalogue_unavailable"
                  ? "browse.unavailable"
                  : "common.error"
              )}
            </Notice>
          )}
          {results.page && total === 0 && (
            <EmptyState
              icon={<SearchIcon />}
              title={t("store.search.empty", { q: query })}
              action={
                <button
                  type="button"
                  className="btn btn--quiet"
                  onClick={() => replace("/")}
                >
                  {t("store.search.clear")}
                </button>
              }
            >
              <p>{t("store.search.emptyHint")}</p>
            </EmptyState>
          )}
          <TitleGrid paged={results} labelledBy="results-h" />
        </section>
      ) : (
        <div className="store" id="store">
          {empty && (
            <EmptyState icon={<FilmIcon />} title={t("store.empty.title")}>
              <p>{t("store.empty.body")}</p>
            </EmptyState>
          )}
          {genres.length > 0 && <GenreChips genres={genres} />}
          {rails.map((rail) => (
            <Rail key={rail.id} rail={rail} />
          ))}
          {/* Members only; absent when the provider published nothing. */}
          <InformationRail />
          {truncated && <p className="muted">{t("browse.truncated")}</p>}
        </div>
      )}
    </>
  );
}
